/* PublicLayout — Layout institucional claro (fundo branco, azul-marinho, dourado).
 * Diferente do Layout interno (militar/escuro), comunica TRANSPARÊNCIA, CIÊNCIA, PROSPECTIVA.
 */
import { Link, NavLink, Outlet } from "react-router-dom";
import { Anchor, Globe2, Info, LogIn, Home } from "lucide-react";

export default function PublicLayout() {
  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col overflow-x-hidden" data-testid="public-layout">
      {/* Top bar institucional */}
      <header className="border-b border-slate-200/80 bg-white/95 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[78px] flex items-center justify-between gap-8">
          <Link to="/" className="flex items-center gap-4 group shrink-0" data-testid="public-logo">
            <div className="w-[58px] h-[58px] rounded-full bg-white flex items-center justify-center shadow-[0_3px_10px_-2px_rgba(10,35,66,0.18)] ring-1 ring-slate-200/80 overflow-hidden transition-transform duration-300 group-hover:scale-[1.03]">
              <img
                src="/assets/logo-nostradamus-oficial.png?v=3"
                alt="Projeto Nostradamus — LSC · EGN"
                className="w-[54px] h-[54px] object-contain"
                draggable="false"
              />
            </div>
            <div className="leading-[1.15] hidden sm:block">
              <div className="text-[10px] tracking-[0.32em] uppercase text-amber-700/90 font-mono font-semibold">LSC · EGN</div>
              <div className="font-display font-bold text-[18px] text-[#0a2342] tracking-[-0.01em] mt-0.5">Projeto Nostradamus</div>
              <div className="text-[10.5px] text-slate-500 font-medium tracking-wide">Observatório Prospectivo</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5 ml-auto mr-2">
            <PublicLink to="/" icon={Home} label="Início" testid="nav-public-home" />
            <PublicLink to="/observatorio" icon={Anchor} label="Observatório" testid="nav-public-observatorio" />
            <PublicLink to="/mapa-publico" icon={Globe2} label="Mapa Público" testid="nav-public-mapa" />
            <PublicLink to="/sobre" icon={Info} label="Sobre" testid="nav-public-sobre" />
          </nav>
          <Link
            to="/login"
            className="group inline-flex items-center gap-2 pl-4 pr-3.5 py-2.5 rounded-md bg-[#0a2342] text-white text-[12.5px] font-semibold tracking-[0.04em] hover:bg-[#142e4f] transition shadow-[inset_0_-2px_0_rgba(245,158,11,0.55)] hover:shadow-[inset_0_-2px_0_rgba(245,158,11,0.85)]"
            data-testid="public-login-button"
          >
            <LogIn size={13} className="text-amber-300" /> Área Restrita
          </Link>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer institucional */}
      <footer className="bg-[#0a2342] text-slate-300 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6 text-xs">
          <div>
            <div className="font-display font-bold text-amber-400 mb-2">Projeto Nostradamus</div>
            <p className="text-slate-400 leading-relaxed">
              Observatório de Sementes de Futuro em Segurança Pública & Defesa do
              Laboratório de Simulações e Cenários da Escola de Guerra Naval (LSC/EGN).
            </p>
          </div>
          <div>
            <div className="font-display font-bold text-amber-400 mb-2">Navegação</div>
            <ul className="space-y-1">
              <li><Link to="/" className="hover:text-white">Início</Link></li>
              <li><Link to="/observatorio" className="hover:text-white">Observatório</Link></li>
              <li><Link to="/mapa-publico" className="hover:text-white">Mapa Público</Link></li>
              <li><Link to="/sobre" className="hover:text-white">Sobre</Link></li>
              <li><Link to="/login" className="hover:text-white">Acesso institucional</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-display font-bold text-amber-400 mb-2">Aviso</div>
            <p className="text-slate-400 leading-relaxed">
              Os dados exibidos representam monitoramento agregado de publicações científicas,
              estratégicas e institucionais relacionadas aos vetores de pesquisa do Projeto Nostradamus.
              As informações são atualizadas periodicamente e podem conter dados simulados em ambiente de demonstração.
            </p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-6 pt-4 border-t border-white/10 text-[10px] font-mono text-slate-500 flex justify-between">
          <span>© {new Date().getFullYear()} LSC/EGN — Escola de Guerra Naval</span>
          <span>v2.0 · Observatório Prospectivo</span>
        </div>
      </footer>
    </div>
  );
}

function PublicLink({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[13px] font-medium tracking-tight transition ${
          isActive
            ? "text-[#0a2342] font-semibold after:content-[''] after:absolute after:left-3.5 after:right-3.5 after:-bottom-[10px] after:h-[2px] after:bg-amber-500"
            : "text-slate-500 hover:text-[#0a2342] hover:bg-slate-50"
        }`
      }
      data-testid={testid}
    >
      <Icon size={14} className="opacity-75" />
      <span>{label}</span>
    </NavLink>
  );
}
